import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** One shortcuts-dialog row: the action text and its key cap. */
interface ShortcutRow {
  readonly keys: string;
  /** i18n key under the shortcuts namespace. */
  readonly actionKey: 'mic' | 'cam' | 'hand';
}

/** The three meeting shortcuts (the mockup's «1–9» prototype row is omitted). */
const SHORTCUTS: readonly ShortcutRow[] = [
  { keys: 'M', actionKey: 'mic' },
  { keys: 'V', actionKey: 'cam' },
  { keys: 'H', actionKey: 'hand' },
];

const KBD_CLASS =
  'rounded border border-white/10 bg-white/[0.08] px-1.5 py-0.5 font-mono text-[11px] text-zinc-200';

export interface ShortcutsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/** The Syncra shortcuts dialog, opened from the control bar's «Ещё» menu. */
export function ShortcutsDialog(props: ShortcutsDialogProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="border-white/10 bg-zinc-900 text-zinc-100 sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold tracking-tight text-white">
            {t('shortcuts.title')}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-zinc-400">
            {t('shortcuts.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="divide-y divide-white/[0.06]">
          {SHORTCUTS.map((shortcut) => (
            <div
              key={shortcut.keys}
              data-testid={`shortcut-row-${shortcut.actionKey}`}
              className="flex items-center justify-between gap-4 py-2.5 first:pt-0 last:pb-0"
            >
              <span className="text-[12.5px] text-zinc-300">
                {t(`shortcuts.action_${shortcut.actionKey}`)}
              </span>
              <kbd className={KBD_CLASS}>{shortcut.keys}</kbd>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
